import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
/**
* @class UserAccount
* @classdesc Class definition for record type from model
* @param {OBJECT} record - record from model to init class
* @extends BCMSRecord
* @example const record = model.userAccounts[0];
* const device = new UserAccount(record);
* @property {NUMBER} _id - The id of the UserAccount
* @property {STRING} _username - The username of the UserAccount
* @property {STRING} _userType - The userType of the UserAccount
* @property {STRING} _userID - The userID of the UserAccount
* @property {STRING} _status - The status of the UserAccount
* @property {BCMSDate} _lastLogin - The lastLogin of the UserAccount
* @property {STRING} _initials - The initials of the UserAccount
* @property {STRING} _first - The first name of the UserAccount
* @property {STRING} _last - The last name of the UserAccount
* @property {STRING} _selectedTimezoneID - The selectedTimezoneID of the UserAccount
* @property {STRING} _password - The password of the UserAccount
* @property {STRING} _confirmPassword - The confirmPassword of the UserAccount
* @property {STRING} _title - The title of the UserAccount
* @property {STRING} _middleName - The middleName of the UserAccount
* @property {STRING} _suffix - The suffix of the UserAccount
* @property {BCMSDate} _birthDate - The birthDate of the UserAccount
* @property {STRING} _ssn - The ssn of the UserAccount
* @property {STRING} _emailAddress - The emailAddress of the UserAccount
* @property {STRING} _homePhoneNumber - The homePhoneNumber of the UserAccount
* @property {STRING} _mobilePhoneNumber - The mobilePhoneNumber of the UserAccount
* @property {STRING} _workPhoneNumber - The workPhoneNumber of the UserAccount
* @property {STRING} _workPhoneNumberExt - The workPhoneNumberExt of the UserAccount
* @property {STRING} _addressLine1 - The addressLine1 of the UserAccount
* @property {STRING} _addressLine2 - The addressLine2 of the UserAccount
* @property {STRING} _city - The city of the UserAccount
* @property {STRING} _state - The state of the UserAccount
* @property {STRING} _zip - The zip of the UserAccount
* @property {BOOLEAN} _isABusinessAdministrator - The isABusinessAdministrator value of the UserAccount
*/

export default class UserAccount extends BCMSRecord {
    _id
    _username
    _userType
    _userID
    _status
    _lastLogin
    _initials
    _first
    _fullName
    _last
    _selectedTimezoneID
    _password
    _confirmPassword
    _title
    _middleName
    _suffix
    _birthDate
    _ssn
    _emailAddress
    _homePhoneNumber
    _mobilePhoneNumber
    _workPhoneNumber
    _workPhoneNumberExt
    _addressLine1
    _addressLine2
    _city
    _state
    _zip
    _isABusinessAdministrator
    
    constructor(record) {     
      super(record)
    }

    /**
     * @name userIsCurrentUser
     * @static
     * @param {UserAccount} userAccount - Instance of a "userAccount" that we want to verify
     * is the current global user of the demo. Right now that is "George Washington" with id "0".
     * @memberof UserAccount
     */
    static userIsCurrentUser(userAccount) {
      return userAccount.id === "0";
    }
  }
  UserAccount.define({
    "recordType":"userAccounts",
    "recordIDProp":"id",
    "className" : "UserAccount",
    "recordProperties":[
        /** 
         * @name id
         * @instance
         * @property get {NUMBER} - gets the id of the UserAccount
         * @property set {NUMBER} - sets the id of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("id", "_id"),

        /** 
         * @name username
         * @instance
         * @property get {NUMBER} - gets the username of the UserAccount
         * @property set {NUMBER} - sets the username of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("username", "_username"),

        /** 
         * @name userType
         * @instance
         * @property get {NUMBER} - gets the userType of the UserAccount
         * @property set {NUMBER} - sets the userType of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("userType", "_userType"),

        /** 
         * @name userID
         * @instance
         * @property get {NUMBER} - gets the userType of the UserAccount
         * @property set {NUMBER} - sets the userType of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("userID", "_userID"),

        /** 
         * @name status
         * @instance
         * @property get {NUMBER} - gets the status of the UserAccount
         * @property set {NUMBER} - sets the status of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("status", "_status"),

        /** 
         * @name lastLogin
         * @instance
         * @property get {NUMBER} - gets the lastLogin of the UserAccount
         * @property set {NUMBER} - sets the lastLogin of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToDate("lastLogin", "_lastLogin"),
        
        /** 
         * @name initials
         * @instance
         * @property get {NUMBER} - gets the initials of the UserAccount
         * @property set {NUMBER} - sets the initials of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("initials", "_initials"),

        /** 
         * @name first
         * @instance
         * @property get {NUMBER} - gets the first of the UserAccount
         * @property set {NUMBER} - sets the first of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("first", "_first"),

        /** 
         * @name fullName
         * @instance
         * @property get {STRING} - gets the fullName
         * @property set {STRING} - sets the fullName
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("fullName", "_fullName"),

        /** 
         * @name last
         * @instance
         * @property get {NUMBER} - gets the last of the UserAccount
         * @property set {NUMBER} - sets the last of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("last", "_last"),

        /** 
         * @name emailAddress
         * @instance
         * @property get {NUMBER} - gets the emailAddress of the UserAccount
         * @property set {NUMBER} - sets the emailAddress of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("emailAddress", "_emailAddress"),

        /** 
         * @name homePhoneNumber
         * @instance
         * @property get {NUMBER} - gets the homePhoneNumber of the UserAccount
         * @property set {NUMBER} - sets the homePhoneNumber of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("homePhoneNumber", "_homePhoneNumber"),

        /** 
         * @name selectedTimezoneID
         * @instance
         * @property get {NUMBER} - gets the selectedTimezoneID of the UserAccount
         * @property set {NUMBER} - sets the selectedTimezoneID of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("selectedTimezoneID", "_selectedTimezoneID"),

        /** 
         * @name password
         * @instance
         * @property get {NUMBER} - gets the password of the UserAccount
         * @property set {NUMBER} - sets the password of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("password", "_password"),

        /** 
         * @name confirmPassword
         * @instance
         * @property get {NUMBER} - gets the confirmPassword of the UserAccount
         * @property set {NUMBER} - sets the confirmPassword of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("confirmPassword", "_confirmPassword"),

        /** 
         * @name title
         * @instance
         * @property get {NUMBER} - gets the title of the UserAccount
         * @property set {NUMBER} - sets the title of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("title", "_title"),

        /** 
         * @name middleName
         * @instance
         * @property get {NUMBER} - gets the middleName of the UserAccount
         * @property set {NUMBER} - sets the middleName of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("middleName", "_middleName"),

        /** 
         * @name suffix
         * @instance
         * @property get {NUMBER} - gets the suffix of the UserAccount
         * @property set {NUMBER} - sets the suffix of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("suffix", "_suffix"),

        /** 
         * @name birthDate
         * @instance
         * @property get {NUMBER} - gets the birthDate of the UserAccount
         * @property set {NUMBER} - sets the birthDate of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("birthDate", "_birthDate"),

        /** 
         * @name ssn
         * @instance
         * @property get {NUMBER} - gets the ssn of the UserAccount
         * @property set {NUMBER} - sets the ssn of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("ssn", "_ssn"),

        /** 
         * @name mobilePhoneNumber
         * @instance
         * @property get {NUMBER} - gets the mobilePhoneNumber of the UserAccount
         * @property set {NUMBER} - sets the mobilePhoneNumber of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("mobilePhoneNumber", "_mobilePhoneNumber"),

        /** 
         * @name workPhoneNumber
         * @instance
         * @property get {NUMBER} - gets the workPhoneNumber of the UserAccount
         * @property set {NUMBER} - sets the workPhoneNumber of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("workPhoneNumber", "_workPhoneNumber"),

        /** 
         * @name workPhoneNumberExt
         * @instance
         * @property get {NUMBER} - gets the workPhoneNumberExt of the UserAccount
         * @property set {NUMBER} - sets the workPhoneNumberExt of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("workPhoneNumberExt", "_workPhoneNumberExt"),

        /** 
         * @name addressLine1
         * @instance
         * @property get {NUMBER} - gets the addressLine1 of the UserAccount
         * @property set {NUMBER} - sets the addressLine1 of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("addressLine1", "_addressLine1"),

        /** 
         * @name addressLine2
         * @instance
         * @property get {NUMBER} - gets the addressLine2 of the UserAccount
         * @property set {NUMBER} - sets the addressLine2 of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("addressLine2", "_addressLine2"),

        /** 
         * @name city
         * @instance
         * @property get {NUMBER} - gets the city of the UserAccount
         * @property set {NUMBER} - sets the city of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("city", "_city"),

        /** 
         * @name state
         * @instance
         * @property get {NUMBER} - gets the state of the UserAccount
         * @property set {NUMBER} - sets the state of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("state", "_state"),

        /** 
         * @name state
         * @instance
         * @property get {NUMBER} - gets the zip of the UserAccount
         * @property set {NUMBER} - sets the zip of the UserAccount
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToString("zip", "_zip"),

        /** 
         * @name isABusinessAdministrator
         * @instance
         * @property get {BOOLEAN} - gets the isABusinessAdministrator value
         * @property set {BOOLEAN} - sets the isABusinessAdministrator value
         * @memberof UserAccount
         */
        BCMSRecordProperty.mapToBoolean("isABusinessAdministrator", "_isABusinessAdministrator"),
      ]
})




